// paint_strokes — peindre des traits sur un canevas. UNE seule définition.
//
// ── POURQUOI CE FICHIER EXISTE ──────────────────────────────────────────────
//
// Les traits sont peints à DEUX moments : pendant qu'on dessine (l'éditeur repeint à chaque
// geste) et au moment d'enregistrer (l'image qui part est redessinée depuis l'originale).
// J'avais écrit les deux, et ils se ressemblaient à une ligne près — exactement le genre de
// duplication qui finit par diverger, et le jour où elle diverge l'image ENVOYÉE ne montre
// pas ce que l'opérateur a dessiné. Il ne le verrait jamais.
//
// Ce n'est pas une fonction pure — elle écrit dans un contexte de canevas — mais c'est UNE
// définition, et c'est ce qui compte ici. Les règles qui se CALCULENT (géométrie, épaisseur,
// filtrage des points) sont dans lib/annotate.ts, pures et éprouvées.
import type { Stroke } from "./annotate";

/** Le contexte, réduit à ce qu'on en emploie. Déclaré ainsi pour que ce module n'exige pas
 *  les définitions du navigateur, et qu'un appelant puisse en fournir un faux. */
export type Painter = {
  lineCap: CanvasLineCap;
  lineJoin: CanvasLineJoin;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  fillStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  beginPath: () => void;
  moveTo: (x: number, y: number) => void;
  lineTo: (x: number, y: number) => void;
  stroke: () => void;
  fill: () => void;
  arc: (x: number, y: number, r: number, a0: number, a1: number) => void;
};

/**
 * Peindre les traits, dans le repère de l'image.
 *
 * BOUTS ET JOINTURES ARRONDIS : un trait à angles droits se lit comme un rectangle mal
 * fait, pas comme un geste de la main.
 *
 * UN TRAIT D'UN SEUL POINT LAISSE UN POINT. `stroke()` sur un chemin d'un seul point ne
 * peint RIEN : un clic simple ne marquerait rien, et l'opérateur croirait le dessin cassé.
 */
export function paintStrokes(ctx: Painter, strokes: readonly Stroke[]): void {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const t of strokes) {
    if (t.points.length === 0) continue;
    ctx.strokeStyle = t.color;
    ctx.fillStyle = t.color;
    ctx.lineWidth = t.width;
    const premier = t.points[0]!;
    if (t.points.length === 1) {
      ctx.beginPath();
      ctx.arc(premier.x, premier.y, Math.max(1, t.width / 2), 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(premier.x, premier.y);
    for (let i = 1; i < t.points.length; i++) ctx.lineTo(t.points[i]!.x, t.points[i]!.y);
    ctx.stroke();
  }
}
